import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { theme } from '../theme';
import { enablePush } from '../pushWeb';

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '7px',
  color: theme.colors.text,
  fontSize: '1rem',
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  color: theme.colors.textMuted,
  marginBottom: '0.4rem',
  fontSize: '0.9rem',
};

function Login({ setUser, setCompanyBlocked, setCompanySaldo, setRoleId, setPerUserCalendar, setHabeasPending, setNotifications }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const resetSuccess = location.state?.resetSuccess;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message);
        return;
      }
      localStorage.setItem('token', data.token);

      // Notificaciones push del navegador, que es lo que hace que la web instalada
      // en la pantalla de inicio de un iPhone avise de citas y pedidos igual que la
      // app de Android.
      //
      // Va justo aquí, y no en un useEffect, porque estamos dentro del submit del
      // formulario: Safari solo permite pedir el permiso de notificaciones durante
      // un gesto real del usuario y lo ignora en silencio en cualquier otro momento.
      //
      // Sin await a propósito: si Firebase tarda o falla, el usuario entra igual.
      enablePush(data.token).then((r) => {
        if (!r.ok) console.info('[Push] No se activó:', r.reason);
      });

      localStorage.setItem('username', data.username);
      localStorage.setItem('role_id', data.role_id);
      localStorage.setItem('company_id', data.company_id);
      localStorage.setItem('company_blocked', data.company_blocked ? 'true' : 'false');
      localStorage.setItem('company_saldo', data.company_saldo ? JSON.stringify(data.company_saldo) : '');
      setCompanyBlocked(!!data.company_blocked);
      setCompanySaldo(data.company_saldo || null);
      setRoleId(data.role_id);
      if (setPerUserCalendar) setPerUserCalendar(!!data.per_user_calendar);
      setHabeasPending(!!data.habeas_pending);
      setNotifications(data.notifications || []);
      setUser(data.username);
      navigate('/');
    } catch {
      setError('Error de conexión. Verifica que el servidor esté corriendo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main style={{ padding: '3rem 1.5rem', display: 'flex', justifyContent: 'center' }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: '14px',
        border: `1px solid ${theme.colors.border}`,
        padding: '2.5rem',
        width: '100%',
        maxWidth: '420px',
        boxShadow: `0 4px 24px ${theme.colors.glowSubtle}`,
      }}>
        <h1 style={{ color: theme.colors.primary, marginBottom: '2rem', textAlign: 'center', fontSize: '1.6rem' }}>
          Iniciar Sesión
        </h1>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.2rem' }}>
            <label style={labelStyle}>Correo electrónico</label>
            <input
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="email"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              style={inputStyle}
            />
          </div>

          <p style={{ textAlign: 'right', margin: '0 0 1.25rem' }}>
            <Link to="/forgot-password" style={{ color: theme.colors.primary, fontSize: '0.85rem', textDecoration: 'none' }}>
              ¿Olvidaste tu contraseña?
            </Link>
          </p>

          {resetSuccess && (
            <div style={{
              backgroundColor: 'rgba(74,222,128,0.1)',
              border: `1px solid ${theme.colors.success}`,
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              marginBottom: '1rem',
              color: theme.colors.success,
              fontSize: '0.88rem',
              textAlign: 'center',
            }}>
              Contraseña actualizada correctamente. Ya puedes iniciar sesión.
            </div>
          )}

          {error && (
            <p style={{ color: theme.colors.error, textAlign: 'center', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: loading ? theme.colors.border : theme.colors.primary,
              color: theme.colors.background,
              border: 'none',
              borderRadius: '7px',
              fontSize: '1rem',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        <p style={{ color: theme.colors.textMuted, textAlign: 'center', marginTop: '1.5rem', fontSize: '0.9rem' }}>
          ¿No tienes cuenta?{' '}
          <Link to="/register" style={{ color: theme.colors.primary, textDecoration: 'none' }}>
            Regístrate
          </Link>
        </p>
      </div>
    </main>
  );
}

export default Login;
